import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  ModalController,
  NavController,
  IonRouterOutlet,
} from '@ionic/angular';
import { StorageService } from '../services/storage.service';
import {
  createPostObject,
  Post,
} from '@jaimo-solutions-gmbh/platform-services-module';
import { openConsent, openFullscreen, openShare } from 'src/helpers';
import { Filter } from '../pages/profile/profile.page';
import { getEntries, getMixedStream } from '../api/entry';
import { isBlacklisted } from 'src/app/utils/blacklist';

interface GroupedPosts {
  filter: Filter;
  label: string;
  posts: Post[];
}

@Component({
  selector: 'app-stream',
  templateUrl: './stream.component.html',
  styleUrls: ['./stream.component.scss'],
})
export class StreamComponent implements OnInit {
  @Output() onFilterChange = new EventEmitter<any>();
  @Output() onReload = new EventEmitter();

  @Input() handles: string | string[];
  @Input() filters: Filter[] = [];
  @Input() activeFilter?: Filter;
  @Input() scrollElement: string;
  @Input() reloadStream: boolean;

  @Input() accountName?: string;
  @Input() singlePost: Post;

  posts: Post[] = [];
  groupedPosts: GroupedPosts[] = [];

  currentHandles: string | string[];

  isLoadingFilters = false;
  isLoadingEntries = false;

  constructor(
    private storageService: StorageService,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private routerOutlet: IonRouterOutlet
  ) {}

  ngOnInit() {}

  ngOnChanges() {
    if (
      String(this.currentHandles) !== String(this.handles) ||
      this.reloadStream
    ) {
      this.currentHandles = this.handles;
      this.getEntries(this.handles, this.activeFilter, null, true);
      if (this.reloadStream) this.onReload.emit();
    }
  }

  goToStage = (handle: string) =>
    this.navCtrl.navigateForward('/' + handle, {
      animated: false,
    });

  openConsent = (url, source) =>
    openConsent(url, !isBlacklisted(url) ? undefined : source, this.modalCtrl);

  openFullscreen = ({ post, index }) =>
    openFullscreen(post, index, this.modalCtrl, this.routerOutlet.nativeEl);

  openShare = (post) => openShare(post, this.modalCtrl);

  getLabel = (date: Date) => {
    const today = new Date().setHours(24, 60, 60, 100);
    const diffInDays = Math.floor(
      (today - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    return diffInDays === 0
      ? 'Heute'
      : diffInDays === 1
      ? 'Gestern'
      : diffInDays === 2
      ? 'Vorgestern'
      : diffInDays < 7
      ? `Vor ${diffInDays} Tagen`
      : diffInDays < 14
      ? 'Vor einer Woche'
      : diffInDays < 21
      ? 'Vor zwei Wochen'
      : diffInDays < 28
      ? 'Vor drei Wochen'
      : diffInDays < 35
      ? 'Vor vier Wochen'
      : diffInDays < 63
      ? 'Vor einem Monat'
      : diffInDays < 91
      ? 'Vor 2 Monaten'
      : diffInDays < 119
      ? 'Vor 3 Monaten'
      : diffInDays < 147
      ? 'Vor 4 Monaten'
      : diffInDays < 175
      ? 'Vor 5 Monaten'
      : diffInDays < 203
      ? 'Vor 6 Monaten'
      : diffInDays < 231
      ? 'Vor 7 Monaten'
      : diffInDays < 259
      ? 'Vor 8 Monaten'
      : diffInDays < 287
      ? 'Vor 9 Monaten'
      : diffInDays < 315
      ? 'Vor 10 Monaten'
      : diffInDays < 343
      ? 'Vor 11 Monaten'
      : diffInDays < 371
      ? 'Vor 12 Monaten'
      : 'Vor einem Jahr';
  };

  groupPosts = (posts: Post[], filter?: Filter) => {
    const groupedPosts =
      filter && this.groupedPosts?.[0]?.filter !== filter
        ? []
        : this.groupedPosts;

    posts.forEach((post) => {
      const createdLabel = this.getLabel(new Date(post.createdAt * 1000));
      const group = groupedPosts.find(
        ({ filter, label }) => filter === filter && label === createdLabel
      );
      if (group) {
        group.posts.push(post);
      } else {
        groupedPosts.push({
          filter,
          label: createdLabel,
          posts: [post],
        });
      }
    });

    return groupedPosts;
  };

  loadEntries = async (
    handles: string | string[],
    filter?: string,
    since?: string
  ) => {
    if (Array.isArray(handles)) {
      return getMixedStream(handles, since);
    }

    return getEntries({
      handle: handles,
      filter,
      since,
    });
  };

  getEntries = async (
    handles: string | string[],
    filter?: Filter,
    filterDate?: string,
    forceReload?: boolean
  ) => {
    if (handles?.length === 0) {
      this.posts = [];
      return;
    }

    const storedStream = this.storageService.getStored(
      'stream',
      handles.toString(),
      filter?.handle
    );

    if (!storedStream || forceReload) {
      this.isLoadingEntries = true;
      this.posts = [];
      this.groupedPosts = [];
      try {
        const data = await this.loadEntries(
          handles,
          filter?.handle,
          filterDate
        );

        const normalized = data.map((item: any) => ({
          ...item,
          duplicate_entries: Array.isArray(item?.duplicate_entries)
            ? item.duplicate_entries
            : [],
        }));

        this.posts = normalized.map(createPostObject);
        this.groupedPosts = this.groupPosts(this.posts, filter);

        this.storageService.saveStreamContent(
          this.handles,
          this.activeFilter?.handle,
          data
        );
      } catch (e) {}
    } else {
      this.posts = this.storageService.getStreamContent(
        this.handles,
        this.activeFilter?.handle
      );
      this.groupedPosts = this.groupPosts(this.posts, filter);
    }
    this.isLoadingEntries = false;
  };

  filterChanged = (filter: Filter) => {
    if (this.isLoadingEntries) return;

    this.activeFilter = filter;
    this.onFilterChange.emit(filter);
    this.getEntries(this.handles, filter);
  };

  loadMore = async () => {
    if (
      this.posts.length === 0 ||
      this.handles.length === 0 ||
      this.isLoadingEntries
    )
      return;

    this.isLoadingEntries = true;

    const time = String(this.posts[this.posts.length - 1].createdAt);

    const data = await this.loadEntries(
      this.handles,
      this.activeFilter?.handle,
      time
    );

    const posts = data.map(createPostObject);
    this.posts = this.posts.concat(posts);
    this.groupedPosts = this.groupPosts(posts, this.activeFilter);

    this.storageService.saveStreamContent(
      this.handles,
      this.activeFilter?.handle,
      data
    );
    this.isLoadingEntries = false;
  };

  explore() {
    this.navCtrl.navigateRoot('/explore');
  }

  //t = current time
  //b = start value
  //c = change in value
  //d = duration
  easeInOutQuad(t, b, c, d) {
    t /= d / 2;
    if (t < 1) {
      return (c / 2) * t * t + b;
    }
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  }

  isDesktop() {
    return window.innerWidth >= 992;
  }
}
